import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteLeague } from '../services/api';

/**
 * Card showing a summary of a saved league.
 * @param {Object} league - LeagueDTO
 * @param {Function} [onDelete] - Called with leagueId after successful deletion
 */
function LeagueListItem({ league, onDelete }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const date = league.createdAt
    ? new Date(league.createdAt).toLocaleDateString()
    : '—';

  function handleCardClick() {
    if (!confirming) {
      navigate(`/league/${league.leagueId}`);
    }
  }

  function handleDeleteClick(e) {
    e.stopPropagation();
    setConfirming(true);
  }

  function handleCancelDelete(e) {
    e.stopPropagation();
    setConfirming(false);
  }

  async function handleConfirmDelete(e) {
    e.stopPropagation();
    setDeleting(true);
    try {
      await deleteLeague(league.leagueId);
      onDelete?.(league.leagueId);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  }

  const scoringLabel = {
    half_ppr: '0.5 PPR',
    ppr: 'PPR',
    standard: 'Standard',
    custom: 'Custom',
  }[league.scoringFormat] || league.scoringFormat;

  return (
    <div
      className='draft-list-item'
      onClick={handleCardClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
    >
      <div className='draft-list-item-header'>
        <span className='draft-list-item-name'>{league.leagueName || 'Untitled League'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className='draft-status-badge active'>
            {(league.sport || 'NFL').toUpperCase()}
          </span>
          {onDelete && !confirming && (
            <button
              className='filter-pill'
              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
              onClick={handleDeleteClick}
              title='Delete league'
            >
              ×
            </button>
          )}
          {confirming && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Delete?</span>
              <button
                className='filter-pill active'
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? '…' : 'Yes'}
              </button>
              <button
                className='filter-pill'
                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                No
              </button>
            </span>
          )}
        </div>
      </div>
      <div className='draft-list-item-meta'>
        <span>{league.seasonYear || '—'}</span>
        <span>{scoringLabel}</span>
        <span>{league.teamCount} teams</span>
        <span>{league.rosterCount} players</span>
        <span>{date}</span>
      </div>
    </div>
  );
}

export default LeagueListItem;
